import React, { ReactElement, useCallback, useContext, useState } from 'react';
import { View } from '@shared/components/View';
import { MBButton } from '@shared/components/button';
import { MBIconV2 } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1 } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { MBMultiSelectProps } from './MBMultiSelect';

interface MBMultiSelectEditProps<T> extends MBMultiSelectProps<T> {
  value: T;
  isArchived?: boolean;
}
export function MBMultiSelectEdit<T>({
  value,
  isArchived,
  labelExtractor,
  onChangeLabel,
  onArchive,
  onUnarchive,
}: MBMultiSelectEditProps<T>): ReactElement {
  const [archived] = useState(isArchived);
  const [text, setText] = useState(labelExtractor?.(value) ?? '');
  const [archiving, setArchiving] = useState(false);
  const handleSave = useCallback(() => onChangeLabel?.(value, text), [onChangeLabel, text, value]);
  const handleUnarchive = useCallback(async () => {
    setArchiving(true);
    await onUnarchive?.(value);
  }, [onUnarchive, value]);
  const handleArchive = useCallback(async () => {
    setArchiving(true);
    await onArchive?.(value);
  }, [onArchive, value]);
  const colorTheme = useContext(ColorThemeContext);

  return (
    <View style={{ justifyContent: 'space-between' }} fill>
      <View>
        <View paddingHorizontal={2} paddingVertical={2}>
          <MBTextInput autoFocus value={text} blurOnSubmit={true} onChangeText={setText} />
        </View>
        <View paddingHorizontal={2} paddingVertical={1}>
          <MBButton title={i18n('common.save')} onPress={handleSave} />
        </View>
      </View>
      <View>
        {archived && (
          <MBTouchableOpacity onPress={handleUnarchive} disabled={archiving}>
            <View
              row
              centerVertically
              centerHorizontally
              paddingHorizontal={3}
              paddingVertical={1}
              // style={{ backgroundColor: colorTheme.input.backgroundActive }}
            >
              <MBIconV2 name="archive-box" size={grid(5)} color={colorTheme.foregroundActive} />
              <View paddingLeft={1}>
                <MBTextBody1 color={colorTheme.foregroundActive}>
                  {archiving ? i18n('common.unarchiving') : i18n('common.unarchive')}
                </MBTextBody1>
              </View>
            </View>
          </MBTouchableOpacity>
        )}
        {!archived && (
          <MBTouchableOpacity onPress={handleArchive} disabled={archiving}>
            <View
              row
              centerVertically
              centerHorizontally
              paddingHorizontal={3}
              paddingVertical={1}
              // style={{ backgroundColor: colorTheme.input.backgroundActive }}
            >
              <MBIconV2 name="archive-box" size={grid(5)} color={colorTheme.button.destructive} />
              <View paddingLeft={1}>
                <MBTextBody1 color={colorTheme.button.destructive}>
                  {archiving ? i18n('common.archiving') : i18n('common.archive')}
                </MBTextBody1>
              </View>
            </View>
          </MBTouchableOpacity>
        )}
      </View>
    </View>
  );
}
